import Cookies from 'js-cookie';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePrepareAuthenticateMutation } from '@app/api/Authentication';
import {
  useLazyGetCaptchaQuery,
  useLazyTestCaptchaQuery,
} from '@app/api/Captcha';
import { WARNING } from '@app/constants';
import { NotificationContext } from '@app/providers';
import { decryptEmailCookie, handleCatchError } from '@app/services';

import { getAuthorizationCookieNames } from '@pages/Authorization/utils/authorizationSession';

import type { ChangeEvent } from 'react';

interface UseAuthorizationPreparedFormProps {
  email: string;
  getEmailFieldValue: () => string;
  isEnabled: boolean;
  setCaptchaFieldValue: (value: string) => void;
  setEmail: (email: string) => void;
  setEmailFieldValue: (value: string) => void;
}

interface UseAuthorizationPreparedFormReturn {
  captchaChangeHandler: ({ target }: ChangeEvent<HTMLInputElement>) => void;
  captchaImage: string;
  clearServerErrorHandler: () => void;
  emailChangeHandler: ({ target }: ChangeEvent<HTMLInputElement>) => void;
  isPageLoader: boolean;
  isSubmitLoader: boolean;
  registerAuthAttemptHandler: () => void;
  resetCaptchaFieldHandler: () => void;
  serverError: string;
  setServerError: (error: string) => void;
  setSubmitLoader: (value: boolean) => void;
  validateCaptchaHandler: () => Promise<boolean>;
}

export const useAuthorizationPreparedForm = ({
  email,
  getEmailFieldValue,
  isEnabled,
  setCaptchaFieldValue,
  setEmail,
  setEmailFieldValue,
}: UseAuthorizationPreparedFormProps): UseAuthorizationPreparedFormReturn => {
  const { showNotification } = useContext(NotificationContext);
  const [searchParams] = useSearchParams();
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [userCaptchaValue, setUserCaptchaValue] = useState('');
  const [serverError, setServerError] = useState('');
  const [isPageLoader, setPageLoader] = useState(false);
  const [isSubmitLoader, setSubmitLoader] = useState(false);
  const [authAttemptVersion, setAuthAttemptVersion] = useState(0);
  const isEmailInitializedRef = useRef(false);

  const [triggerGetCaptchaQuery] = useLazyGetCaptchaQuery();
  const [triggerTestCaptchaQuery] = useLazyTestCaptchaQuery();
  const [prepareAuthenticate] = usePrepareAuthenticateMutation();

  const clearServerErrorHandler = useCallback(() => {
    setServerError('');
  }, []);

  const emailChangeHandler = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setEmail(target.value);

      if (serverError) {
        setServerError('');
      }
    },
    [serverError, setEmail]
  );

  const captchaChangeHandler = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setUserCaptchaValue(target.value);

      if (serverError) {
        setServerError('');
      }
    },
    [serverError]
  );

  const initializeEmailHandler = useCallback(async () => {
    if (!isEnabled || isEmailInitializedRef.current || email) {
      return;
    }

    try {
      const emailFromParam = String(searchParams.get('email') || '');

      if (emailFromParam) {
        setEmailFieldValue(emailFromParam);
        setEmail(emailFromParam);
        isEmailInitializedRef.current = true;
        return;
      }

      const { emailCookieName } = getAuthorizationCookieNames();
      const emailCookie = Cookies.get(emailCookieName);

      if (!emailCookie) {
        isEmailInitializedRef.current = true;
        return;
      }

      const fetchedEmail = await decryptEmailCookie({ emailCookie });

      setEmailFieldValue(fetchedEmail);
      setEmail(fetchedEmail);
    } catch (error) {
      setServerError(handleCatchError(error));
    } finally {
      isEmailInitializedRef.current = true;
    }
  }, [email, isEnabled, searchParams, setEmail, setEmailFieldValue]);

  useEffect(() => {
    void initializeEmailHandler();
  }, [initializeEmailHandler]);

  useEffect(() => {
    if (!isEnabled || !email) {
      return;
    }

    if (getEmailFieldValue() !== email) {
      setEmailFieldValue(email);
    }
  }, [email, getEmailFieldValue, isEnabled, setEmailFieldValue]);

  const prepareAuthenticationHandler = useCallback(async () => {
    if (!isEnabled) {
      return;
    }

    setPageLoader(true);

    try {
      // IP устройства бэк определяет сам — параметров у метода больше нет
      // (us/11114).
      const attemptsCount = await prepareAuthenticate().unwrap();

      if (typeof attemptsCount === 'number' && attemptsCount > 3) {
        const { data: captchaValue, error: getCaptchaError } =
          await triggerGetCaptchaQuery({ minLifetime: 60 });

        if (captchaValue) {
          setCaptchaImage(captchaValue.Captcha ?? '');
          setCaptchaId(captchaValue.Id ?? '');
        }

        if (getCaptchaError && 'data' in getCaptchaError) {
          setServerError(`${getCaptchaError.data}`);
        }
      }
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setPageLoader(false);
    }
  }, [
    isEnabled,
    prepareAuthenticate,
    showNotification,
    triggerGetCaptchaQuery,
  ]);

  useEffect(() => {
    void prepareAuthenticationHandler();
  }, [authAttemptVersion, prepareAuthenticationHandler]);

  const validateCaptchaHandler = useCallback(async () => {
    if (!captchaImage) {
      return true;
    }

    const numericCaptchaValue = Number(userCaptchaValue);

    if (Number.isNaN(numericCaptchaValue)) {
      setServerError('Результат вычисления должен быть числом!');
      return false;
    }

    const { data: testCaptchaResult } = await triggerTestCaptchaQuery({
      captchaId,
      formulaResult: numericCaptchaValue,
      minLifetime: 60,
    });

    if (!testCaptchaResult?.TestResult) {
      setServerError('Результат вычисления уравнения указан неверно.');
      setCaptchaFieldValue('');
      return false;
    }

    return true;
  }, [
    captchaId,
    captchaImage,
    setCaptchaFieldValue,
    triggerTestCaptchaQuery,
    userCaptchaValue,
  ]);

  const registerAuthAttemptHandler = useCallback(() => {
    setAuthAttemptVersion((previousValue) => previousValue + 1);
  }, []);

  const resetCaptchaFieldHandler = useCallback(() => {
    setUserCaptchaValue('');
    setCaptchaFieldValue('');
  }, [setCaptchaFieldValue]);

  return {
    captchaChangeHandler,
    captchaImage,
    clearServerErrorHandler,
    emailChangeHandler,
    isPageLoader,
    isSubmitLoader,
    registerAuthAttemptHandler,
    resetCaptchaFieldHandler,
    serverError,
    setServerError,
    setSubmitLoader,
    validateCaptchaHandler,
  };
};
